/**
 * Drive (Files) layout primitives: the screen frame (keyboard target), breadcrumb that collapses to the parent on a
 * phone, the body with its scrolling main column, the list table (columns hide as the list narrows), grid, thumbnail,
 * item frame (drag a selection onto a folder, modifier clicks, double-click), side panels (details, versions), upload
 * tray, preview frame, storage meter and an audio player. Native: SwiftUI `List`/`LazyVGrid` + `.draggable`/
 * `.dropDestination`, `.inspector`; Compose `LazyColumn`/`LazyVerticalGrid` + `dragAndDropSource`/`Target`.
 */
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { useMediaSrc } from "./media.tsx";
import { Spinner } from "./Spinner.tsx";
import "./drive.css";

/** The DataTransfer type a dragged Drive selection travels under (its keys as JSON). */
const DRAG_TYPE = "application/x-bundle-drive";

export function DriveFrame({ onKeyDown, testID, children }: { onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void; testID?: string; children: ReactNode }) {
  return (
    <div className="b-drive-screen" data-testid={testID} tabIndex={-1} onKeyDown={onKeyDown}>
      {children}
    </div>
  );
}

/**
 * "My files › Design › Q4 launch ▾": `crumbs` are the ancestors (root first), each followed by a separator; on a phone
 * only the parent stays. With no crumbs only `current` shows (a view's title).
 */
export function DriveCrumbs({ label, crumbs, current }: { label: string; crumbs: { key: string; node: ReactNode }[]; current: ReactNode }) {
  return (
    <div className="b-drive-crumbs-wrap">
      <nav aria-label={label} className="b-drive-crumbs">
        {crumbs.map((c, n) => (
          <span key={c.key} className={n < crumbs.length - 1 ? "b-drive-crumb-far" : "b-drive-crumb"}>
            {c.node}
            <span className="b-drive-crumb-sep" aria-hidden="true">
              ›
            </span>
          </span>
        ))}
        {current}
      </nav>
    </div>
  );
}

/** The row under the toolbars: the main column and, beside it, a side panel. */
export function DriveBody({ children }: { children: ReactNode }) {
  return <div className="b-drive-body">{children}</div>;
}

/** The scrolling list/grid column. A press on its empty background calls `onBackgroundPress` (clear the selection). */
export function DriveMain({ onBackgroundPress, children }: { onBackgroundPress?: () => void; children: ReactNode }) {
  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- background press only; Escape clears from the keyboard
    <div
      className="b-drive-main"
      onClick={(e) => {
        if (e.target === e.currentTarget) onBackgroundPress?.();
      }}
    >
      {children}
    </div>
  );
}

export type DriveClickModifiers = { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean };

export type DriveItemFrameProps = {
  /** The keys a drag of this item carries (its selection), or undefined when it can't be dragged. */
  dragKeys?: () => string[];
  /** Dragged keys dropped here (a folder you can edit); undefined = not a drop target. */
  onDropKeys?: (keys: string[]) => void;
  /** Runs before the item's own press with the click's modifier keys. */
  onClickModifiers?: (m: DriveClickModifiers) => void;
  onContextMenu?: () => void;
  onDoubleClick?: () => void;
  testID?: string;
  children: ReactNode;
};

/** One list row or grid card: drag source, folder drop target, modifier clicks, double-click to open. */
export function DriveItemFrame({ dragKeys, onDropKeys, onClickModifiers, onContextMenu, onDoubleClick, testID, children }: DriveItemFrameProps) {
  const [over, setOver] = useState(false);
  const dropOk = (types: readonly string[]) => onDropKeys !== undefined && types.includes(DRAG_TYPE);
  return (
    <div
      className="b-drive-item"
      data-drop={over ? "true" : undefined}
      draggable={dragKeys !== undefined}
      onDragStart={(e) => {
        if (dragKeys === undefined) return;
        e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(dragKeys()));
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => {
        if (!dropOk(Array.from(e.dataTransfer.types))) return;
        e.preventDefault();
        e.stopPropagation();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!dropOk(Array.from(e.dataTransfer.types))) return;
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        let keys: string[] = [];
        try {
          keys = JSON.parse(e.dataTransfer.getData(DRAG_TYPE) || "[]") as string[];
        } catch {
          keys = [];
        }
        onDropKeys?.(keys);
      }}
      onClickCapture={(e) => onClickModifiers?.({ shiftKey: e.shiftKey, metaKey: e.metaKey, ctrlKey: e.ctrlKey })}
      onContextMenuCapture={onContextMenu}
      onDoubleClick={onDoubleClick}
      data-testid={testID}
    >
      {children}
    </div>
  );
}

export type DriveColumns = { name: ReactNode; owner: ReactNode; modified: ReactNode; size: ReactNode; actions?: ReactNode };

/** The list: a header row and rows. Owner and size hide when the list is narrow, then modified. */
export function DriveTable({ label, testID, children }: { label: string; testID?: string; children: ReactNode }) {
  return (
    <div role="table" aria-label={label} data-testid={testID}>
      {children}
    </div>
  );
}

export function DriveTableRow({ header, selected, cells }: { header?: boolean; selected?: boolean; cells: DriveColumns }) {
  return (
    <div className={header ? "b-drive-head" : "b-drive-row"} role="row" aria-selected={header ? undefined : selected}>
      <div className="b-drive-cell b-drive-col-name">{cells.name}</div>
      <div className="b-drive-cell b-drive-col-owner">{cells.owner}</div>
      <div className="b-drive-cell b-drive-col-modified">{cells.modified}</div>
      <div className="b-drive-cell b-drive-col-size">{cells.size}</div>
      <div className="b-drive-cell">{cells.actions}</div>
    </div>
  );
}

/** Folder tiles (`folders`) or file cards with thumbnails (`files`), filling the width. */
export function DriveGrid({ variant, children }: { variant: "folders" | "files"; children: ReactNode }) {
  return <div className={variant === "folders" ? "b-drive-grid-folders" : "b-drive-grid"}>{children}</div>;
}

/** A card's thumbnail well (an image, or the type's icon centred). */
export function DriveThumb({ children }: { children: ReactNode }) {
  return <div className="b-drive-thumb">{children}</div>;
}

/** The details pane beside the list, or the versions panel beside a preview (a sheet over the content on a phone). */
export function DriveSidePanel({ variant, label, testID, children }: { variant: "details" | "versions"; label: string; testID?: string; children: ReactNode }) {
  return (
    <aside className={variant === "details" ? "b-drive-details" : "b-drive-versions"} aria-label={label} data-testid={testID}>
      {children}
    </aside>
  );
}

/** The upload tray, bottom-right (full width on a phone): a header, a scrolling list and a footer (both hidden when collapsed). */
export function DriveTray({
  label,
  testID,
  header,
  collapsed,
  footer,
  children,
}: {
  label: string;
  testID?: string;
  header: ReactNode;
  collapsed?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="b-drive-tray" aria-label={label} data-testid={testID}>
      {header}
      {collapsed ? null : (
        <>
          <div className="b-drive-tray-list">{children}</div>
          {footer}
        </>
      )}
    </section>
  );
}

/** The preview over the list: a header bar, the stage (the viewer, centred) and an optional side panel. */
export function DrivePreviewFrame({
  label,
  testID,
  header,
  stage,
  side,
}: {
  label: string;
  testID?: string;
  header: ReactNode;
  stage: ReactNode;
  side?: ReactNode;
}) {
  return (
    <div className="b-drive-preview" role="dialog" aria-modal="true" aria-label={label} data-testid={testID}>
      {header}
      <div className="b-drive-preview-body">
        <div className="b-drive-preview-stage">{stage}</div>
        {side}
      </div>
    </div>
  );
}

/** The storage meter: `percent` used, coloured by `level`. */
export function DriveMeter({ percent, level, label }: { percent: number; level: "ok" | "warning" | "full"; label: string }) {
  return (
    <div className="b-drive-meter" data-level={level} role="img" aria-label={label}>
      <div style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
    </div>
  );
}

/** A native audio player for a (possibly mock) media URL. */
export function AudioPlayer({ src, title, testID }: { src: string; title: string; testID?: string }) {
  const url = useMediaSrc(src);
  return url === undefined ? <Spinner /> : <audio controls src={url} aria-label={title} data-testid={testID} />;
}
